"use client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { FC, useEffect, useState } from "react";
import LessonCheckIcon from "./LessonCheckIcon";
import LessonLockIcon from "./LessonLockIcon";
import ProgressRing from "./ProgressRing";

interface LessonItem {
  id: number;
  title: string;
  completed?: boolean; // 新增完成状态
  locked?: boolean; // 新增锁定状态
}
interface ChapterItem {
  id: number;
  title: string;
  lessons: LessonItem[];
  progress?: number; // 新增进度百分比
  completedCount?: number; // 完成数量
  totalCount?: number; // 总数量
}

interface CourseSidebarProps {
  catalog: ChapterItem[];
}

const CourseSidebar: FC<CourseSidebarProps> = ({ catalog }) => {
  const theme = useTheme();
  const t = useTranslations("CitadelCourse");
  const router = useRouter();
  const params = useParams();
  const currentLessonId = params.lessonId ? Number(params.lessonId) : null;
  // 初始化 openChapters
  const [openChapters, setOpenChapters] = useState<{ [key: number]: boolean }>({});

  // 处理课程点击事件
  const handleLessonClick = (lessonId: number) => {
    // 导航到指定课程页面
    router.push(`/products/citadel/course/${lessonId}`);
  };

  const handleToggle = (idx: number) => {
    setOpenChapters((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // 初始化展开状态，根据当前lessonId决定展开哪个章节
  useEffect(() => {
    // 找到当前lessonId对应的章节索引
    let targetChapterIdx = -1;
    if (currentLessonId) {
      targetChapterIdx = catalog.findIndex((chapter) =>
        chapter.lessons.some((lesson) => lesson.id === currentLessonId)
      );
    }

    // 如果没有找到当前lesson对应的章节，则使用原来的逻辑（找第一个有未完成课程的章节）
    if (targetChapterIdx === -1) {
      targetChapterIdx = 0;
    }

    const obj: { [key: number]: boolean } = {};
    catalog.forEach((_, idx) => {
      obj[idx] = idx === targetChapterIdx;
    });

    setOpenChapters(obj);
  }, [catalog, currentLessonId]);

  return (
    <Box
      sx={{
        width: "22.5rem",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        gap: "1.75rem",
      }}>
      {catalog.map((chapter, idx) => {
        // 在渲染时计算每章的进度
        const completedCount = chapter.lessons.filter((lesson: LessonItem) => lesson.completed).length;
        const totalCount = chapter.lessons.length;
        const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

        return (
          <Box
            key={chapter.id}
            sx={{
              bgcolor: "background.paperMedium",
              borderRadius: "0.75rem",
              px: 1.25,
              py: 2.5,
              boxShadow: "none",
              border: "none",
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 1,
              }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: "var(--font-size-xs)",
                    lineHeight: 1.6,
                    mb: 0.5,
                  }}>
                  {chapter.title}
                </Typography>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "var(--font-size-xs)",
                  }}>
                  {t("sidebar.lessonProgress", {
                    completed: completedCount,
                    total: totalCount,
                  })}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: 56,
                  height: 56,
                  ml: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <ProgressRing progress={progress} />
              </Box>
            </Box>
            {/* Lessons */}
            {chapter.lessons.length > 0 && (
              <Box
                sx={{
                  overflow: "hidden",
                  transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1)",
                  maxHeight: openChapters[idx] !== false ? 1000 : 0,
                }}>
                {chapter.lessons.map((lesson) => {
                  if (!lesson.locked) {
                    return (
                      <Box
                        key={lesson.id}
                        tabIndex={0}
                        role="button"
                        aria-disabled={false}
                        sx={{
                          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#FFF",
                          borderRadius: "0.5rem",
                          mt: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          border: "2px solid transparent",
                          boxShadow: "none",
                          cursor: "pointer",
                          transition: "background 0.2s, box-shadow 0.2s, transform 0.15s cubic-bezier(0.4,0,0.2,1)",
                          outline: "none",
                          userSelect: "none",
                          "&:hover": {
                            boxShadow: 2,
                          },
                          "&:active": {
                            bgcolor: "background.paperMedium",
                            boxShadow: 6,
                            transform: "scale(0.98)",
                          },
                        }}
                        onClick={() => handleLessonClick(lesson.id)}>
                        {lesson.completed ? (
                          <LessonCheckIcon size="1.25rem" style={{ marginRight: "0.625rem", flexShrink: 0 }} />
                        ) : null}
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "var(--font-size-xs)",
                            fontWeight: 600,
                            textAlign: "left",
                          }}>
                          {lesson.title}
                        </Typography>
                      </Box>
                    );
                  }
                  // locked
                  return (
                    <Box
                      key={lesson.id}
                      sx={{
                        bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#FFF",
                        borderRadius: "0.5rem",
                        mt: 1,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        border: "2px solid transparent",
                        boxShadow: "none",
                        cursor: "default",
                        transition: "background 0.2s, opacity 0.2s",
                      }}>
                      <Box
                        sx={{
                          fontSize: "1rem",
                          color: "text.tertiary",
                          mr: "0.625rem",
                          flexShrink: 0,
                        }}>
                        <LessonLockIcon />
                      </Box>
                      <Typography
                        sx={{
                          color: "text.tertiary",
                          fontSize: "var(--font-size-xs)",
                          fontWeight: 400,
                        }}>
                        {lesson.title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
                height: 0,
              }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translate(-50%, 0%)", // 中心在下边界
                  bgcolor: "background.paperMedium",
                  borderRadius: "1.5rem",
                  px: 2.5,
                  pt: 1,
                  pb: 0.2,
                  display: "flex",
                  alignItems: "center",
                  fontSize: "var(--font-size-xs)",
                }}
                onClick={() => handleToggle(idx)}>
                {openChapters[idx] !== false ? (
                  <ExpandLessIcon sx={{ fontSize: "1.5rem" }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: "1.5rem" }} />
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export function findNextLesson(catalog: ChapterItem[], currentLessonId: number): LessonItem {
  let found = false;
  let nextLesson: any = null;
  for (let i = 0; i < catalog.length; i++) {
    const lessons = catalog[i].lessons;
    for (let j = 0; j < lessons.length; j++) {
      if (lessons[j].id === currentLessonId) {
        found = true;
        // 优先找同章节下一个课程
        if (j + 1 < lessons.length) {
          nextLesson = lessons[j + 1];
        } else {
          // 如果当前章节已是最后一课，则找下一个章节的第一个课程
          for (let k = i + 1; k < catalog.length; k++) {
            if (catalog[k].lessons.length > 0) {
              nextLesson = catalog[k].lessons[0];
              break;
            }
          }
        }
        break;
      }
    }
    if (found) break;
  }
  if (!nextLesson) {
    return catalog[0].lessons[0]; // 如果没有找到，返回第一个课程
  }
  return nextLesson;
}

export default CourseSidebar;
